import {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, useTheme } from "@mui/material";
import { HotTable } from "@handsontable/react";
import { registerAllModules } from "handsontable/registry";
import type { AreaRow } from "./types";
import { validateAreaRow } from "./validation";
import { createColorRenderer } from "./renderers";
import { buildTableStyles } from "./styles";
import ColorPickerPopover from "./colorPicker";

registerAllModules();

export interface ShapeElementsTableHandle {
  getRows: () => AreaRow[];
}

type ShapeKind = "area";

export interface ShapeElementsTableProps {
  kind: ShapeKind;
  data: AreaRow[];
  onDataChange: (rows: AreaRow[]) => void;
}

const COLOR_PROPS: Record<ShapeKind, string[]> = {
  area: ["color", "fillColor"],
};

const PolygonsTable = forwardRef<
  ShapeElementsTableHandle,
  ShapeElementsTableProps
>(function PolygonsTable({ kind, data, onDataChange }, ref) {
  const theme = useTheme();
  const hotRef = useRef<any>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(0);

  useImperativeHandle(ref, () => ({
    getRows: () =>
      (hotRef.current?.hotInstance?.getSourceData() as AreaRow[]) || [],
  }));

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => setTableHeight(el.clientHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const kindRef = useRef(kind);
  useEffect(() => {
    kindRef.current = kind;
  }, [kind]);

  const onDataChangeRef = useRef(onDataChange);
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  const [colorPopover, setColorPopover] = useState<{
    anchorEl: HTMLElement | null;
    row: number;
    prop: string;
  } | null>(null);

  const emitRevalidated = useCallback(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const allData = hot.getSourceData() as any[];
    allData.forEach((row) => {
      row._errors = validateAreaRow(row);
    });
    onDataChangeRef.current([...allData]);
  }, []);

  const handleAfterChange = useCallback(
    (changes: any[] | null, source: string) => {
      if (!changes || source === "loadData" || source === "internal_update")
        return;
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;

      const modeChanged = changes.some(
        ([, prop, oldV, newV]: [number, string, any, any]) =>
          prop === "mode" && oldV !== newV,
      );
      if (modeChanged && !hot.isDestroyed) hot.render();

      emitRevalidated();
    },
    [emitRevalidated],
  );

  const handleToggleSelectAll = useCallback(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const allData = hot.getSourceData() as any[];
    const allChecked =
      allData.length > 0 && allData.every((row) => row.checked);
    allData.forEach((row) => {
      row.checked = !allChecked;
    });
    onDataChangeRef.current([...allData]);
  }, []);

  const handleAfterGetColHeader = useCallback(
    (col: number, TH: HTMLElement) => {
      if (!TH) return;

      const hot = hotRef.current?.hotInstance;
      const prop = hot?.colToProp(col) as string | undefined;
      if (prop && COLOR_PROPS[kindRef.current].includes(prop)) {
        TH.classList.add("em-col-center");
      }

      if (col !== 0) return;
      const span = (TH.querySelector(".colHeader") as HTMLElement | null) || TH;
      let checkbox = span.querySelector(
        "input.ht-select-all",
      ) as HTMLInputElement | null;
      if (!checkbox) {
        span.textContent = "";
        checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.className = "ht-select-all";
        span.appendChild(checkbox);
      }
      const allData = (hot?.getSourceData() as any[]) || [];
      const total = allData.length;
      const checked = allData.filter((r) => r.checked).length;
      checkbox.checked = total > 0 && checked === total;
      checkbox.indeterminate = checked > 0 && checked < total;
    },
    [],
  );

  const handleBeforeOnCellMouseDown = useCallback(
    (event: any, coords: any, TD: any) => {
      const hotInstance = hotRef.current?.hotInstance;
      if (!hotInstance) return;

      if (coords.row === -1) {
        if (coords.col === 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          handleToggleSelectAll();
        }
        return;
      }
      if (coords.row < 0) return;

      const prop = hotInstance.colToProp(coords.col) as string;
      if (!COLOR_PROPS[kindRef.current].includes(prop)) return;

      const sel = hotInstance.getSelectedRangeLast();
      const alreadySelected =
        sel &&
        coords.row >= sel.from.row &&
        coords.row <= sel.to.row &&
        coords.col >= sel.from.col &&
        coords.col <= sel.to.col;
      if (!alreadySelected) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      setColorPopover({ anchorEl: TD, row: coords.row, prop });
    },
    [handleToggleSelectAll],
  );

  const colorRenderer = useMemo(() => createColorRenderer(), []);

  const columns = useMemo(() => {
    const checkboxCol = {
      data: "checked",
      title: "",
      type: "checkbox",
      width: 22,
      className: "htCenter htMiddle",
    };

    return [
      checkboxCol,
      {
        data: "titulo",
        title: "Título",
        type: "text",
        width: 160,
      },
      {
        data: "color",
        title: "Borda",
        width: 60,
        editor: false,
        renderer: colorRenderer,
      },
      {
        data: "fillColor",
        title: "Preenchimento",
        width: 60,
        editor: false,
        renderer: colorRenderer,
      },
    ];
  }, [colorRenderer]);

  const cellsConfig = useCallback(
    (row: number, _col: number, prop: string | number) => {
      const cellProperties: any = {};

      return cellProperties;
    },
    [],
  );

  const customStyles = useMemo(() => buildTableStyles(theme), [theme]);

  useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const rafId = requestAnimationFrame(() => {
      if (!hot.isDestroyed) hot.render();
    });
    return () => cancelAnimationFrame(rafId);
  }, []);

  const currentColorValue =
    colorPopover && hotRef.current?.hotInstance
      ? ((
          hotRef.current.hotInstance.getSourceDataAtRow(colorPopover.row) as any
        )?.[colorPopover.prop] ?? "")
      : "";

  return (
    <>
      <style>{customStyles}</style>
      <Box
        ref={wrapperRef}
        className="custom-table-wrapper"
        sx={{ height: "100%", overflow: "hidden", width: "100%" }}
      >
        <HotTable
          ref={hotRef}
          data={data}
          licenseKey="non-commercial-and-evaluation"
          rowHeaders={false}
          width="100%"
          height={tableHeight || undefined}
          stretchH="all"
          cells={cellsConfig}
          columns={columns}
          afterChange={handleAfterChange}
          beforeOnCellMouseDown={handleBeforeOnCellMouseDown}
          afterGetColHeader={handleAfterGetColHeader}
          manualColumnResize={true}
          rowHeights={32}
          className="custom-table-wrapper"
        />
      </Box>

      <ColorPickerPopover
        open={Boolean(colorPopover)}
        anchorEl={colorPopover?.anchorEl ?? null}
        value={currentColorValue}
        onClose={() => setColorPopover(null)}
        onChange={(next) => {
          const hot = hotRef.current?.hotInstance;
          if (hot && colorPopover) {
            hot.setDataAtRowProp(
              colorPopover.row,
              colorPopover.prop,
              next,
              "colorPicker",
            );
          }
        }}
      />
    </>
  );
});

export default PolygonsTable;
